import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { FC, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import checkCircle from '../../../Components/Assets/checkCircle.svg';
import close from '../../../Components/Assets/close.svg';
import errorFilled from '../../../Components/Assets/errorFilled.svg';
import ContentType from '../../../Core/Common/ContentTypes';
import FileExtension from '../../../Core/Common/FileExtensions';
import applicationConstants from '../../../Core/Utility/ApplicationConstants';
import LinkButton, { LinkButtonType } from '../../Components/Form/Buttons/LinkButton';
import useSearchParameters from '../../Components/Navigation/Hooks/useSearchParametersHook';
import useNotificationHook from '../../Components/Notifications/useNotificationHook';
import browserService from '../../Services/BrowserService';
import useLanguage from '../../Services/Language/useLanguageHook';
import { IUserData } from './UserDataDefinitions';
import UserDataExternalLink from './UserDataExternalLink';
import UserDataForm from './UserDataForm';
import styles from './UserDataPage.module.scss';

const UserDataUpload: FC = () => {
    const [translations] = useLanguage();
    const [uploadFormOpen, setUploadFormOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | undefined>();
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [, setSearchParameters] = useSearchParameters();
    const navigate = useNavigate();
    const [displayError] = useNotificationHook();

    const validateFile = (file: File | undefined) => {
        if (!file) {
            return null;
        }

        const validMimeTypes = [ContentType.Csv, ContentType.Excel];
        const validExtensions = [FileExtension.Csv, FileExtension.Xlsx];
        if (!validMimeTypes.includes(file.type)) {
            return translations.userData.upload.invalidFile;
        }
        if (!validExtensions.some((x) => file.name.endsWith(x))) {
            return translations.userData.upload.invalidFile;
        }

        return null;
    };

    const handleBack = () => {
        navigate(-1);
    };

    const handleFileSelect = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileUpload = (userData: IUserData) => {
        if (selectedFile) {
            setUploadFormOpen(false);
            const url = selectedFile.type === ContentType.Excel ? '/demeter/user-data/excel' : '/demeter/user-data';
            browserService
                .postWithFile(
                    url,
                    {
                        name: userData.name,
                        currency: userData.currency,
                        unitOfMeasure: userData.unitOfMeasure,
                    },
                    selectedFile!,
                )
                .catch((reason: any) => {
                    const statusCode = reason?.response?.status;
                    if (statusCode === applicationConstants.HttpStatus.BadRequest) {
                        const errorMessage = reason?.response?.data?.detail;
                        if (errorMessage) {
                            displayError(errorMessage);
                        } else {
                            displayError(translations.userData.upload.error);
                        }
                    } else {
                        displayError(translations.userData.upload.error);
                    }
                })
                .finally(() => {
                    setSearchParameters({ show: 'Management' });
                });
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        const validationError = validateFile(file);
        setError(validationError);
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleFileDelete = () => {
        setSelectedFile(undefined);
        setError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className={styles.master_page_container} data-track="Upload">
            <div className={styles.user_data_file_select}>
                <div className={styles.user_data_file_select_header_container}>
                    <LinkButton
                        title={translations.actions.back}
                        onClick={handleBack}
                        type={LinkButtonType.White}
                        icon={<ArrowBackIcon />}
                        track={{ eventCategory: 'UserData', eventAction: 'BackClicked' }}
                    />
                    <h2>{translations.userData.upload.header}</h2>
                </div>
                <div className={styles.user_data_file_select_container}>
                    <div className={styles.user_data_file_select_container_description}>
                        <Trans i18nKey="userData.upload.description" components={{ UserDataExternalLink: <UserDataExternalLink /> }} />
                    </div>
                    <div className={styles.user_data_file_select_container_form}>
                        <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />
                        <LinkButton
                            title={translations.userData.upload.chooseFile}
                            onClick={handleFileSelect}
                            type={LinkButtonType.White}
                            track={{ eventCategory: 'UserData', eventAction: 'ChooseFileClicked' }}
                        />
                        {selectedFile && (
                            <div className={styles.user_data_file_select_container_form_file}>
                                {!error && <img src={checkCircle} alt="" className={styles.user_data_file_select_container_form_file_icon_success} />}
                                {error && <img src={errorFilled} alt="" className={styles.user_data_file_select_container_form_file_icon_error} />}
                                <div className={styles.user_data_file_select_container_form_file_name}>
                                    {selectedFile.name}
                                    {error && <div className={styles.user_data_file_select_container_form_file_error}>{error}</div>}
                                </div>
                                <button type="button" onClick={handleFileDelete}>
                                    <img src={close} alt="" className={styles.user_data_file_select_container_form_file_icon_delete} />
                                </button>
                            </div>
                        )}
                        {selectedFile && (
                            <LinkButton
                                title={translations.userData.upload.import}
                                disabled={error !== null}
                                onClick={() => setUploadFormOpen(true)}
                                type={LinkButtonType.Blue}
                                track={{ eventCategory: 'UserData', eventAction: 'ImportClicked' }}
                            />
                        )}
                    </div>
                </div>
            </div>
            <UserDataForm open={uploadFormOpen} onClose={() => setUploadFormOpen(false)} onChange={handleFileUpload} />
        </div>
    );
};
export default UserDataUpload;
